import { useMemo, useState, useEffect, useRef } from "react";
import "../../accounting/accounting.css";
import "../../modules/SettingsDetailPage.css"; // full-page `.sd-*` detail chrome
import "./cityPage.css";
import { AppDataGrid, gridExportMatrix, type AppColumn } from "../../../components/AppDataGrid";
import { AiCombo } from "../../../components/AiCombo";
import { AppToolbar, ExportMenu } from "../../../components/AppToolbar";
import { FormTitle } from "../../../components/FormTitle";
import { ViewToggle } from "../../../components/ViewToggle";
import { useNotify } from "../../../components/notifications";
import { useSaveFlow, useConfirm, useReasonGate } from "../../../components/confirm";
import { useHotkeys, formatCombo } from "../../../components/hotkeys";
import { useFetchMs } from "../../../hooks/useFetchMs";
import { api, runWithAuditReason } from "../../../api/client";
import {
  useCities,
  useCityStates,
  useSaveCity,
  useDeleteCity,
  errMessage,
  type CityRow,
  type StagedArea,
} from "./city";
import { CityAreasPanel } from "./CityAreasPanel";

interface Props {
  onHome?: () => void;
}

/** State dropdown label — the State name (LongName), matching LIMS's CustomState
 *  lookup (which displays the "State" / LongName), and the grid's State column.
 *  Falls back to the short code only when there's no long name. */
function stateLabel(s: { shortName: string; longName: string | null }): string {
  return (s.longName ?? "").trim() || s.shortName;
}

/**
 * Settings ▸ System Management ▸ City — mirrors the legacy XAF `City_ListView`
 * (+ `City_DetailView`): a master grid of cities (City name + State) with New /
 * Edit / Delete. State is a lookup to dbo.CustomState (shown as the short code,
 * e.g. "TX"). New / Edit opens a FULL-PAGE detail (BO UI Standard §4, standing
 * rule 12 — converted 2026-09-26 from the old right-sized popup) carrying the
 * City + State fields and the Area child grid (City_DetailView ▸ Area).
 */
export function CityPage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm, alert } = useConfirm();
  const gateReason = useReasonGate();
  const { data: rows = [], isFetching, refetch } = useCities();
  const { data: states = [] } = useCityStates();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveCity();
  const del = useDeleteCity();

  // Areas staged in memory for a brand-new (unsaved) city (committed on Save).
  const [staged, setStaged] = useState<StagedArea[]>([]);

  const [search, setSearch] = useState("");

  // Page-level maximize (Payment Terms Setup pattern): hides the title + toolbar, keeps the search row with
  // Export + Restore so the grid fills the page; Esc restores.
  const [gridMax, setGridMax] = useState(false);
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setGridMax(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gridMax]);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  // The rows in the order the grid shows them (sorted / filtered) — "Detail View" with nothing ticked opens the top one.
  const shownRef = useRef<{ id: string }[]>([]);
  // Detail view: undefined = list, null = New, row = Edit.
  const [editing, setEditing] = useState<CityRow | null | undefined>(undefined);
  const [name, setName] = useState("");
  const [stateId, setStateId] = useState("");
  const [nameErr, setNameErr] = useState("");

  // Detail view/edit mode (standing rule 12): a row opens READ-ONLY (view) with an
  // Edit button; the toolbar Edit / New open straight to the Save (edit) mode.
  const [mode, setMode] = useState<"view" | "edit">("view");
  const prime = (r: CityRow | null) => {
    setName(r?.cityName ?? "");
    setStateId(r?.stateId ?? "");
    setNameErr("");
    setStaged([]);
  };
  const openNew = () => {
    setMode("edit");
    setEditing(null);
    prime(null);
  };
  const openEdit = (r: CityRow) => {
    setMode("edit");
    setEditing(r);
    prime(r);
  };
  const openView = (r: CityRow) => {
    setMode("view");
    setEditing(r);
    prime(r);
  };

  const close = () => {
    setEditing(undefined);
    setStaged([]);
  };
  const resetToNew = () => {
    setEditing(null);
    setName("");
    setStateId("");
    setNameErr("");
    setStaged([]);
  };

  /** Save the City (create or update), commit any staged areas, then act per the
   *  button used: 'stay' (keep open — LIMS Save), 'close' (Save and Close),
   *  'new' (Save and New). */
  const doSave = async (after: "stay" | "close" | "new") => {
    if (!name.trim()) {
      setNameErr("'City' must not to be empty.");
      return;
    }
    setNameErr("");
    const wasNew = !editing?.id;
    // Reason-for-change gate (standing rule 11) — only on an EDIT (existing
    // record); a brand-new City is a Create (audit-exempt, no reason).  null =
    // user cancelled the reason prompt → abort the save.
    let reason = "";
    if (!wasNew) {
      const r = await gateReason("City", "edit");
      if (r === null) return;
      reason = r;
    }
    let newId: string | undefined;
    await runSave({
      save: async () => {
        await runWithAuditReason(reason, async () => {
          const res = await save.mutateAsync({ id: editing?.id, cityName: name.trim(), stateId: stateId || null });
          newId = editing?.id ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
          // Commit areas staged on a brand-new city (link existing / create new + link).
          if (wasNew && newId && staged.length) {
            for (const s of staged) {
              if (s.areaId) await api.post(`/api/v1/cities/${newId}/areas/link`, { areaId: s.areaId });
              else await api.post(`/api/v1/cities/${newId}/areas`, { areaName: s.name });
            }
          }
        });
      },
      successMessage: "Saved successfully.",
      onSaved: () => {
        if (after === "close") {
          close();
          return;
        }
        if (after === "new") {
          resetToNew();
          return;
        }
        // 'stay': a new city switches to edit mode (areas now committed); an existing edit stays open.
        if (wasNew && newId) {
          const st = states.find((s) => s.id === stateId);
          setEditing({ id: newId, cityName: name.trim(), stateId: stateId || null, state: st ? stateLabel(st) : null });
          setStaged([]);
        }
      },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const removeSelected = async () => {
    if (selectedIds.size === 0) {
      notify.warning("Select at least one city to delete.");
      return;
    }
    const ok = await confirm({
      title: "Delete",
      tone: "danger",
      confirmLabel: "Delete",
      message: `Delete ${selectedIds.size} cit${selectedIds.size === 1 ? "y" : "ies"}?`,
    });
    if (!ok) return;
    // Reason-for-change gate (standing rule 11) — prompts only when the City form
    // has ReasonRequired ON; null = cancelled → abort the delete.
    const reason = await gateReason("City", "delete");
    if (reason === null) return;
    try {
      await runWithAuditReason(reason, async () => {
        for (const id of selectedIds) await del.mutateAsync(String(id));
      });
      setSelectedIds(new Set());
      await alert({ title: "Success", message: "Deleted.", tone: "success" });
    } catch (e) {
      notify.error(errMessage(e));
    }
  };

  // Keyboard shortcuts for the full-page detail header (standing rule 14). VIEW
  // mode mirrors Edit / Close; EDIT mode mirrors Save / Save & New / Save & Close /
  // Cancel.
  useHotkeys(
    editing === undefined
      ? []
      : mode === "view"
        ? [
            { combo: "alt+e", label: "Edit", group: "City", order: 1, run: () => setMode("edit") },
            { combo: "f2", label: "Edit", group: "City", order: 1, run: () => setMode("edit") },
            { combo: "esc", label: "Close", group: "City", order: 2, allowInInput: false, run: () => close() },
          ]
        : [
            { combo: "mod+s", label: "Save", group: "City", order: 1, run: () => void doSave("stay") },
            { combo: "mod+shift+s", label: "Save & New", group: "City", order: 2, run: () => void doSave("new") },
            { combo: "mod+enter", label: "Save & Close", group: "City", order: 3, run: () => void doSave("close") },
            { combo: "esc", label: "Cancel", group: "City", order: 4, allowInInput: false, run: () => close() },
          ],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.cityName, r.state].some((v) => (v ?? "").toLowerCase().includes(q)));
  }, [rows, search]);

  const textCmp = (get: (r: CityRow) => string | null) => (a: CityRow, b: CityRow) => {
    const av = (get(a) ?? "").trim(),
      bv = (get(b) ?? "").trim();
    if (av === "" && bv === "") return 0;
    if (av === "") return -1;
    if (bv === "") return 1;
    return av.localeCompare(bv, undefined, { sensitivity: "base" });
  };

  // Toolbar Edit: the ONE ticked row opens in the Detail View, editable (owner request 2026-10-14 — the
  // in-row editor was removed; the row icon opens the same Detail View).
  const editSelected = () => {
    if (selectedIds.size !== 1) return;
    const [id] = [...selectedIds];
    const r = visible.find((x) => x.id === id);
    if (r) openEdit(r);
  };

  const columns: AppColumn<CityRow>[] = [
    {
      field: "detailedit",
      noExport: true,
      header: "",
      sortable: false,
      filterable: false,
      width: 56,
      align: "center",
      text: () => "",
      // Opens the full Detail View in edit mode (owner request 2026-10-14; was the in-row editor).
      render: (r) => (
        <button
          className="pts-icon-btn edit"
          title="Edit"
          aria-label={`Edit ${r.cityName ?? "city"}`}
          onClick={(e) => {
            e.stopPropagation();
            openEdit(r);
          }}
        >
          <i className="ti ti-edit" />
        </button>
      ),
    },
    {
      field: "cityName",
      header: "City",
      // City and State share the grid half and half (owner request 2026-10-09); with the
      // fitted layout below the two equal widths act as equal proportions.
      width: 600,
      text: (r) => r.cityName ?? "",
      compare: textCmp((r) => r.cityName),
      render: (r) => r.cityName ?? "",
    },
    {
      field: "state",
      header: "State",
      width: 600,
      sortable: false,
      text: (r) => r.state ?? "",
      render: (r) => r.state ?? "",
    },
  ];

  // ── Full-page detail (New / Edit) — replaces the list while front (standing
  //    rule 12). City + State side-label fields + the Area child grid. ──
  if (editing !== undefined) {
    const viewOnly = mode === "view";
    // Record name shown in the title ("City – Crosby") and breadcrumb tail —
    // just the city name, matching the shared SettingsDetailPage (QC Role) design.
    const recordName = editing ? editing.cityName?.trim() || "City" : "New City";
    // Prev/next record arrows (SettingsDetailPage / QC Role parity) — walk the
    // current list; disabled on a brand-new (unsaved) record and while saving.
    const curIdx = editing ? rows.findIndex((r) => r.id === editing.id) : -1;
    const prevRow = curIdx > 0 ? rows[curIdx - 1] : null;
    const nextRow = curIdx >= 0 && curIdx < rows.length - 1 ? rows[curIdx + 1] : null;
    const goTo = (r: CityRow) => (viewOnly ? openView(r) : openEdit(r));

    // Action bar — TOP + BOTTOM (identical set), rendered exactly like the shared
    // SettingsDetailPage's ActionBar: view → Edit · Close; edit → the Save trio.
    const Actions = ({ bottom }: { bottom?: boolean }) => (
      <div className={`sd-actionbar${bottom ? " bottom" : ""}`}>
        {viewOnly ? (
          <>
            <button
              className="sd-abtn primary"
              onClick={() => setMode("edit")}
              title={`Edit (${formatCombo("alt+e")})`}
            >
              <span className="tb-i tb-i-blue">✎</span> Edit
            </button>
            <button className="sd-abtn cancel" onClick={close} title={`Close (${formatCombo("esc")})`}>
              <span className="tb-i">↩︎</span> Close
            </button>
          </>
        ) : (
          <>
            <button
              className="sd-abtn primary"
              onClick={() => void doSave("stay")}
              disabled={save.isPending}
              title={`Save (${formatCombo("mod+s")})`}
            >
              <span className="tb-i">💾</span> Save
            </button>
            <button
              className="sd-abtn"
              onClick={() => void doSave("close")}
              disabled={save.isPending}
              title={`Save and Close (${formatCombo("mod+enter")})`}
            >
              <span className="tb-i">💾</span> Save and Close
            </button>
            <button
              className="sd-abtn"
              onClick={() => void doSave("new")}
              disabled={save.isPending}
              title={`Save and New (${formatCombo("mod+shift+s")})`}
            >
              <span className="tb-i">📄</span> Save and New
            </button>
            <span className="sd-abtn-sep" aria-hidden="true" />
            <button
              className="sd-abtn cancel"
              onClick={close}
              disabled={save.isPending}
              title={`Cancel (${formatCombo("esc")})`}
            >
              <span className="tb-i">↩︎</span> Cancel
            </button>
          </>
        )}
      </div>
    );

    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
        {/* List/Detail toggle (BO UI Standard) — "List View" returns to the grid. */}
        <div style={{ padding: "10px 16px 0", flex: "0 0 auto" }}>
          <ViewToggle
            view="detail"
            onChange={(v) => {
              if (v === "list") close();
            }}
          />
        </div>
        <div className="bl-page mlv-host">
          <div className="bl-grid-area">
            <div className="sd-wrap">
              {/* Header — plain title + breadcrumb + prev/next record arrows, matching
                the shared SettingsDetailPage (QC Role) Detail View. NO Audit chip in
                the Detail View (owner request 2026-09-26); the audit log stays on the
                list-view toolbar. */}
              <div className="sd-head">
                <div>
                  <h2>City – {recordName}</h2>
                  <div className="sd-bc">
                    <a onClick={onHome}>Home</a>
                    <i className="ti ti-chevron-right" />
                    <span>Settings</span>
                    <i className="ti ti-chevron-right" />
                    <span>System Management</span>
                    <i className="ti ti-chevron-right" />
                    <a onClick={close}>City</a>
                    <i className="ti ti-chevron-right" />
                    <span>{recordName}</span>
                  </div>
                </div>
                <div className="sd-navarrows">
                  <button
                    className="sd-na"
                    title="Previous record"
                    disabled={!prevRow || save.isPending}
                    onClick={() => prevRow && goTo(prevRow)}
                  >
                    ‹
                  </button>
                  <button
                    className="sd-na next"
                    title="Next record"
                    disabled={!nextRow || save.isPending}
                    onClick={() => nextRow && goTo(nextRow)}
                  >
                    ›
                  </button>
                </div>
              </div>

              <Actions />

              <div className="sd-body">
                <h3 className="sd-section-h">General Information</h3>
                <div className="sd-grid sd-grid--single sd-grid--side sd-grid--square city-fields">
                  <div className="sd-row">
                    <label className="sd-label">
                      City<span className="req">*</span>
                    </label>
                    <input
                      className={`sd-input${viewOnly ? " readonly" : ""}`}
                      autoFocus={!viewOnly}
                      value={name}
                      readOnly={viewOnly}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (nameErr) setNameErr("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void doSave("stay");
                      }}
                    />
                    {nameErr && <span className="sd-field-error">{nameErr}</span>}
                  </div>
                  <div className="sd-row">
                    <label className="sd-label">State</label>
                    <div className="sd-ms">
                      <AiCombo
                        value={stateId}
                        disabled={viewOnly}
                        options={states.map((s) => ({ value: s.id, label: stateLabel(s) }))}
                        onChange={(v) => setStateId(v ?? "")}
                      />
                    </div>
                  </div>
                </div>

                {/* Area child collection — mirrors City_DetailView ▸ Area. On a new
                  city areas stage in memory (staged/onStaged) and commit on Save;
                  on an existing city they commit immediately. Read-only in view mode. */}
                <div style={{ marginTop: 16 }}>
                  <CityAreasPanel cityId={editing?.id} staged={staged} onStaged={setStaged} readOnly={viewOnly} />
                </div>

                <Actions bottom />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`acc-page${gridMax ? " acc-fullgrid" : ""}`}>
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          <span>System Management</span>
          <i className="ti ti-chevron-right" />
          <span style={{ color: "#4a3728", fontWeight: 600 }}>City</span>
        </div>
        {/* Breadcrumb on top, then the List/Detail toggle beside the FormTitle — the Payment Terms Setup layout
            (owner request 2026-10-15; the toggle used to sit above the breadcrumb). "Detail View" opens the selected
            row (or the first row) in the full-page detail. FormTitle stays ON the grey page — never inside the card;
            the "N items" line was removed (owner request 2026-10-15). */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
          <ViewToggle
            view="list"
            detailDisabled={rows.length === 0}
            onChange={(v) => {
              if (v !== "detail") return;
              const id = selectedIds.size === 1 ? String([...selectedIds][0]) : (shownRef.current[0] ?? rows[0])?.id;
              const r = rows.find((x) => x.id === id);
              if (r) openView(r);
            }}
          />
          <FormTitle pageId="sm-city" loadMs={loadMs ?? null} loading={isFetching} auditFormName="City" />
        </div>
      </div>

      <div className="acc-body city-list acc-search-tight">
        {/* Action toolbar — its OWN full-width row below the title (BO UI Standard §6).
            Canonical order via the shared <AppToolbar>: New · Edit · Delete · Refresh ·
            Export to, then the Loaded/Audit meta chips — ALL left-aligned (owner request
            2026-09-26). Export goes through `exportData` (left flow) instead of the old
            right-slot spacer, which pushed Export + chips to the far right. */}
        {!gridMax && (
        <AppToolbar
          onNew={openNew}
          onEdit={editSelected}
          editDisabled={selectedIds.size !== 1}
          onDelete={() => void removeSelected()}
          deleteDisabled={selectedIds.size === 0}
          onRefresh={() => void refetch()}
          refreshing={isFetching}
          exportData={{
            filename: "City",
            headers: ["City", "State"],
            rows: visible.map((r) => [r.cityName ?? "", r.state ?? ""]),
          }}
        />
        )}
        {/* ONE white grid card — the grid only; its Export / Maximize sit on the search row above. */}
        {/* Search + Export + Maximize on ONE row above the grid card (Payment Terms Setup / ModuleListView layout,
            owner request 2026-10-14) — stays visible while maximized, so Restore is always reachable. */}
        <div className="list-search-row">
          <div className="list-search" data-unsaved-ignore>
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" aria-label="Search" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch("")}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="City" {...gridExportMatrix(visible, columns)} />
            <button type="button" className={`dv-gridact${gridMax ? " dv-on" : ""}`} onClick={() => setGridMax((v) => !v)}
              title={gridMax ? "Restore" : "Maximize"}>
              <i className={`ti ti-arrows-${gridMax ? "minimize" : "maximize"}`} />
            </button>
          </div>
        </div>
        <div className="acc-card city-list">
          <AppDataGrid<CityRow>
            rows={visible}
            columns={columns}
            getRowId={(r) => r.id}
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            onVisibleRowsChange={(r) => { shownRef.current = r; }}
            onRowClick={(r) => openView(r)}
            initialSort={{ key: "cityName", dir: "asc" }}
            loading={isFetching}
            emptyText="No cities found."
            fixedLayout
          hideHeaderTools
          />
        </div>
      </div>
    </div>
  );
}
